import { useState } from "react";
import { motion } from "framer-motion";
import { Coffee, Hotel, Building2, MapPin, Navigation } from "lucide-react";
import { Reveal } from "../Reveal";
import { Eyebrow } from "../bits";

const TYPES = {
  cafe: { label: "Café", icon: Coffee },
  hotel: { label: "Hotel", icon: Hotel },
  coworking: { label: "Coworking", icon: Building2 },
};

const VENUES = [
  { id: "v1", name: "Blackbird Coffee", type: "cafe", dist: "0.2 mi", surfaces: 14, x: 28, y: 34 },
  { id: "v2", name: "The Meridian Hotel", type: "hotel", dist: "0.5 mi", surfaces: 62, x: 58, y: 22 },
  { id: "v3", name: "Loft Coworking", type: "coworking", dist: "0.7 mi", surfaces: 40, x: 44, y: 58 },
  { id: "v4", name: "Corner Roasters", type: "cafe", dist: "0.9 mi", surfaces: 8, x: 74, y: 48 },
  { id: "v5", name: "Harbor Suites", type: "hotel", dist: "1.3 mi", surfaces: 88, x: 20, y: 70 },
  { id: "v6", name: "Depot Workspace", type: "coworking", dist: "1.6 mi", surfaces: 55, x: 80, y: 74 },
];

export default function VenueMap() {
  const [filter, setFilter] = useState("all");
  const [active, setActive] = useState("v2");

  const list = VENUES.filter((v) => filter === "all" || v.type === filter);

  return (
    <section className="bg-navy px-5 py-24 md:px-8 md:py-32">
      <div className="mx-auto max-w-6xl">
        <Reveal className="mb-10">
          <Eyebrow>Roaming · charge near you</Eyebrow>
          <h2 className="mt-4 max-w-2xl font-display text-4xl font-black leading-[1.05] tracking-tight text-white sm:text-5xl">
            Your devices charge everywhere ChargeRevo is.
          </h2>
          <p className="mt-4 max-w-xl text-lg text-revo-ice">
            Roaming is included in every plan. Walk into any Revo-equipped café, hotel, or coworking space and your enrolled devices just start charging — metered to your account, wherever you are.
          </p>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="overflow-hidden rounded-3xl border border-white/10 bg-navy-deep" data-testid="poc-venue-map">
            <div className="grid lg:grid-cols-[1.5fr_1fr]">
              {/* MAP */}
              <div
                className="relative min-h-[380px] overflow-hidden border-b border-white/10 lg:border-b-0 lg:border-r"
                style={{
                  backgroundImage:
                    "linear-gradient(rgba(199,212,238,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(199,212,238,0.06) 1px, transparent 1px)",
                  backgroundSize: "44px 44px",
                }}
              >
                {/* faux roads */}
                <div className="absolute left-0 top-1/3 h-[3px] w-full bg-white/5" />
                <div className="absolute left-1/2 top-0 h-full w-[3px] bg-white/5" />
                <div className="absolute left-0 top-[68%] h-[2px] w-full -rotate-2 bg-white/5" />

                <span className="absolute left-4 top-4 flex items-center gap-1.5 rounded-full bg-revo-green/15 px-3 py-1 text-xs font-bold text-revo-green">
                  <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-revo-green" /> Live · {list.length} nearby
                </span>

                {/* you-are-here */}
                <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
                  <span className="relative flex h-4 w-4">
                    <span className="absolute inline-flex h-full w-full rounded-full bg-white/40" style={{ animation: "ring-expand 2.4s ease-out infinite" }} />
                    <span className="relative inline-flex h-4 w-4 rounded-full border-2 border-white bg-navy" />
                  </span>
                </div>

                {list.map((v) => {
                  const Icon = TYPES[v.type].icon;
                  const isActive = active === v.id;
                  return (
                    <button
                      key={v.id}
                      onClick={() => setActive(v.id)}
                      data-testid={`venue-pin-${v.id}`}
                      className="absolute -translate-x-1/2 -translate-y-1/2"
                      style={{ left: `${v.x}%`, top: `${v.y}%` }}
                      aria-label={v.name}
                    >
                      {isActive && (
                        <span className="absolute left-1/2 top-1/2 h-10 w-10 -translate-x-1/2 -translate-y-1/2 rounded-full border border-revo-green/60" style={{ animation: "ring-expand 2s ease-out infinite" }} />
                      )}
                      <motion.span
                        whileHover={{ scale: 1.15 }}
                        className={`relative flex h-9 w-9 items-center justify-center rounded-full border-2 transition-colors ${
                          isActive ? "border-revo-green bg-revo-green text-navy" : "border-revo-green/40 bg-navy text-revo-green"
                        }`}
                      >
                        <Icon className="h-4 w-4" />
                      </motion.span>
                    </button>
                  );
                })}
              </div>

              {/* LIST */}
              <div className="p-6">
                <div className="mb-4 flex flex-wrap gap-2" data-testid="venue-filters">
                  {["all", "cafe", "hotel", "coworking"].map((t) => (
                    <button
                      key={t}
                      onClick={() => setFilter(t)}
                      data-testid={`venue-filter-${t}`}
                      className={`rounded-full px-3 py-1.5 text-xs font-semibold capitalize transition-colors ${
                        filter === t ? "bg-revo-green text-navy" : "border border-white/15 text-revo-ice hover:border-revo-green"
                      }`}
                    >
                      {t === "all" ? "All" : TYPES[t].label}
                    </button>
                  ))}
                </div>

                <div className="max-h-[320px] space-y-2 overflow-y-auto pr-1" data-lenis-prevent>
                  {list.map((v) => {
                    const Icon = TYPES[v.type].icon;
                    const isActive = active === v.id;
                    return (
                      <button
                        key={v.id}
                        onClick={() => setActive(v.id)}
                        data-testid={`venue-item-${v.id}`}
                        className={`flex w-full items-center gap-3 rounded-2xl border p-3 text-left transition-colors ${
                          isActive ? "border-revo-green bg-revo-green/10" : "border-white/10 bg-white/[0.03] hover:border-white/25"
                        }`}
                      >
                        <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${isActive ? "bg-revo-green text-navy" : "bg-navy text-revo-green"}`}>
                          <Icon className="h-5 w-5" />
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className="truncate font-display text-sm font-bold text-white">{v.name}</p>
                          <p className="flex items-center gap-1 text-xs text-revo-ice/60"><MapPin className="h-3 w-3" />{v.dist} · {v.surfaces} surfaces</p>
                        </div>
                        <Navigation className={`h-4 w-4 ${isActive ? "text-revo-green" : "text-revo-ice/30"}`} />
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
